<template>
  <section
    v-if="showText"
    ref="htmlParent"
    class="tariffs-text container"
    data-test="seo-texts"
    :class="{ full: textShowAll }"
    v-html="newHtml"
  />
  <button
    v-if="!disabledPortal && !textShowAll"
    class="container d-flex justify-center tariffs-text__showAll"
    @click="textShowAll = true"
  >
    <img
      loading="lazy"
      src="@/assets/images/svg/arrow-expand.svg"
      alt="arrow"
    >
  </button>
</template>

<script setup lang="ts">
import { useTariffs } from '~/store/tariffs/tariffs.store'
import { textAggReplace } from '~/helpers'
import { useMainStore } from '~/store/main/main.store'
import { useCities } from '~/store/cites/cities.store'

const props = defineProps({
  html: {
    type: String,
    required: true,
  },
  textAgg: {},
})

const ctx = useNuxtApp()
const htmlParent = ref()
const $router = useRouter()
const mainStore = useMainStore()
const cityStore = useCities()
const disabledPortal = computed(() => mainStore.disabledPortal)
const textShowAll = ref(false)
const showText = ref(true)

const showDialogCity = () => {
  ctx.$event('cityDialog', { redirect: true, label: 'tariffs-text' })
}
const showDialogAddress = () => {
  ctx.$event('addressDialog', { redirect: true, label: 'cheap-steps' })
}

const tariffsStore = useTariffs()
const getAddressText = computed(() => tariffsStore.getAddressText)
const newHtml = computed(() => {
  return textAggReplace(props.html, getAddressText.value, props.textAgg)
    .replace(/style="[^"]*"/g, ``)
    .replace(/<a(?!.*?class=)/g, `<a class="secondary-dark--text"`)
    .replace(
      /<p\s*class="headline-2"\s*>(.*?)<\/p>/g,
      '<h2 class="headline-2">$1</h2>',
    )
    .replace(/<h2>/g, `<h2 class="headline-2">`)
    .replace(/<p>/g, `<p class="title-5">`)
    .replace(
      /%CITY_LOC_BTN%/,
      `<span ref="changeCity" class="pointer secondary-dark--text">${getAddressText.value.cityOnlyLoc}</span>`,
    )
})

const goTo = (e: any, el: any) => {
  e.preventDefault()
  $router.push({ path: el.pathname })
}

onMounted(() => {
  if (htmlParent.value) {
    const changeCity = htmlParent.value.querySelector('span[ref="changeCity"]')
    const changeAddress = htmlParent.value.querySelector(
      'span[ref="changeAddress"]',
    )
    if (changeCity)
      changeCity.addEventListener('click', () => showDialogCity())
    if (changeAddress)
      changeAddress.addEventListener('click', () => showDialogAddress())
    const url = htmlParent.value.querySelectorAll('a')
    if (url) {
      url.forEach((el: any) => {
        if (!el.getAttribute('href')?.startsWith('tel:')) {
          el.addEventListener('click', (e: any) => {
            goTo(e, el)
          })
        }
      })
    }
    if (!disabledPortal.value) {
      htmlParent.value
    }
  }
})
onUnmounted(() => {
  if (htmlParent.value) {
    const changeCity = htmlParent.value.querySelector('span[ref="changeCity"]')
    const url = htmlParent.value.querySelectorAll('a')
    if (changeCity)
      changeCity.removeEventListener('click', () => showDialogCity())
    if (url) {
      url.forEach((el: any) =>
        el.removeEventListener('click', (e: any) => {
          goTo(e, el)
        }),
      )
    }
  }
})
</script>

<style scoped lang="scss">
.tariffs-text {
  padding-top: 40px;
  padding-bottom: 28px;
  overflow: hidden;
  color: color(gray-b);

  @media (max-width: getBreakpoint(desktop)) {
    max-height: 470px;
    padding-bottom: 32px;
    transition: max-height 0.3s;
  }
  &.full {
    max-height: 3000px;
  }

  &__showAll {
    display: none;
    @media (max-width: getBreakpoint(desktop)) {
      top: -20px;
      position: relative;
      background: linear-gradient(0deg, color(white) 50%, transparent 100%);
      width: 100%;
      display: flex;
      align-items: center;
      height: 30px;
      justify-content: center;
    }
  }

  &:deep(h2) {
    margin-bottom: 8px;
    color: color(black);
  }

  &:deep(.tariffs-text__subtitle) {
    margin-bottom: 24px;
    color: color(gray-b);
  }
  &:deep(ul),
  &:deep(ol) {
    padding-left: 20px;
    margin-bottom: 20px;
  }
  &:deep(.tariffs-text__columns-2) {
    list-style: none;
    columns: 2;
    -webkit-columns: 2;
    -moz-columns: 2;
    @media (max-width: getBreakpoint(desktop)) {
      columns: 1;
      -webkit-columns: 1;
      -moz-columns: 1;
    }
    li {
      break-inside: avoid-column;
      position: relative;
      padding-left: 6px;
      padding-right: 20px;
      &:not(:last-child) {
        margin-bottom: 12px;
      }
      &:before {
        content: "";
        position: absolute;
        left: -24px;
        top: 0;
        width: 20px;
        height: 20px;
        mask-image: url('@/assets/images/svg/ischecked.svg');
        background: color(primary);
      }
      .d-block {
        margin-top: 4px;
        margin-bottom: 4px;
      }
    }
  }
  &:deep(ul) {
    list-style: unset;
    li {
    }
    &.columns-2 {
      list-style: none;
      columns: 2;
      -webkit-columns: 2;
      -moz-columns: 2;
      @media (max-width: getBreakpoint(desktop)) {
        columns: 1;
        -webkit-columns: 1;
        -moz-columns: 1;
      }
      li {
        position: relative;
        padding-left: 6px;
        padding-right: 20px;
        &:before {
          content: "";
          position: absolute;
          left: -24px;
          top: 0;
          width: 20px;
          height: 20px;
          background: url("@/assets/images/svg/ischecked.svg");
        }
        p {
          margin-top: 4px;
        }
      }
    }
  }
  &:deep(p:not(.tariffs-text__subtitle)) {
    color: color(gray-b);
    margin-bottom: 8px;
  }
  &:deep(.secondary-dark--text) {
    cursor: pointer;
    transition: color 0.24s;
    &:hover {
      color: color(primary);
    }
  }
}
</style>
